import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Users, UserPlus, Search, RefreshCw, Building2 } from 'lucide-react';
import { toast } from 'sonner';

const TEAMS = [
  { id: 'Servall', name: 'Servall Team', color: 'bg-red-100 text-red-800' },
  { id: 'R2G', name: 'R2G Team', color: 'bg-blue-100 text-blue-800' },
  { id: 'Shivaay', name: 'Shivaay Team', color: 'bg-green-100 text-green-800' },
];

const TeamsPage = () => {
  const { api, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [teamStats, setTeamStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTeam, setFilterTeam] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [newTeam, setNewTeam] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [usersRes, statsRes] = await Promise.all([
        api.get('/users'),
        api.get('/teams/stats'),
      ]);
      setUsers(usersRes.data);
      setTeamStats(statsRes.data.teamStats);
    } catch (err) {
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAssignTeam = async () => {
    if (!selectedUser || !newTeam) return;
    
    try {
      await api.put(`/users/${selectedUser.id}/team`, { team: newTeam });
      toast.success(`${selectedUser.name} assigned to ${newTeam} team`);
      setShowAssignDialog(false);
      setSelectedUser(null);
      setNewTeam('');
      fetchData();
    } catch (err) {
      toast.error('Failed to assign team');
    }
  };

  const openAssignDialog = (user) => {
    setSelectedUser(user);
    setNewTeam(user.team || 'Servall');
    setShowAssignDialog(true);
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         u.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTeam = filterTeam === 'all' || u.team === filterTeam;
    return matchesSearch && matchesTeam;
  });

  const getTeamColor = (team) => {
    const t = TEAMS.find(t => t.id === team);
    return t?.color || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6" data-testid="teams-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">Team Management</h1>
          <p className="text-muted-foreground">Manage team assignments for all users</p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Team Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {teamStats.map((team) => (
          <Card 
            key={team.team} 
            className={`card-hover cursor-pointer ${filterTeam === team.team ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setFilterTeam(filterTeam === team.team ? 'all' : team.team)}
            data-testid={`team-stat-${team.team.toLowerCase()}`}
          >
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  {team.team} Team
                </span>
                <Badge className={getTeamColor(team.team)}>{team.userCount} Users</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-blue-600">{team.leadCount}</p>
                  <p className="text-xs text-muted-foreground">Total Leads</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-600">{team.convertedLeads}</p>
                  <p className="text-xs text-muted-foreground">Converted</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-primary">{team.conversionRate}%</p>
                  <p className="text-xs text-muted-foreground">Rate</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Users by Team</CardTitle>
          <CardDescription>Click on a user to change their team assignment</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="user-search"
              />
            </div>
            <Select value={filterTeam} onValueChange={setFilterTeam}>
              <SelectTrigger className="w-[180px]" data-testid="team-filter">
                <SelectValue placeholder="All Teams" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Teams</SelectItem>
                {TEAMS.map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Users Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Team</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">Loading...</TableCell>
                  </TableRow>
                ) : filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No users found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((u) => (
                    <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                      <TableCell className="font-medium">{u.name}</TableCell>
                      <TableCell className="text-muted-foreground">{u.email}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{u.role}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={getTeamColor(u.team)}>{u.team || 'Unassigned'}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={u.isActive ? 'default' : 'secondary'}>
                          {u.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => openAssignDialog(u)}
                          data-testid={`assign-team-${u.id}`}
                        >
                          <UserPlus className="h-4 w-4 mr-1" />
                          Change Team
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Assign Team Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Team</DialogTitle>
            <DialogDescription>
              Change team assignment for {selectedUser?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={newTeam} onValueChange={setNewTeam}>
              <SelectTrigger data-testid="new-team-select">
                <SelectValue placeholder="Select Team" />
              </SelectTrigger>
              <SelectContent>
                {TEAMS.map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>Cancel</Button>
            <Button onClick={handleAssignTeam} data-testid="confirm-assign-btn">
              Assign to {newTeam} Team
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamsPage;
